/* eslint-disable max-lines */
import { unsafeCoerce } from 'fp-ts/lib/function';
import { orderBy } from 'lodash';
import { createContext, useContext, useMemo, useCallback, useState } from 'react';
import {
  BookingFormClient,
  useDisplayAppointment,
} from '../../bookings/util/useDisplayAppointment';
import { useRouter } from '../../common/hooks/useRouter';
import { DisplayAppointment } from '../../common/schema/hannibal/types.generated';
import {
  useCorporateGroupIdentityConfig,
  CorporateGroupIdentityConfigResult,
} from '../CorporateGroupIdentityConfigProvider/CorporateGroupIdentityConfigProvider';
import {
  useCorporateUserData,
  CorporateUserResult,
} from '../CorporateUserProvider/CorporateUserProvider';
import { useGeneration } from '../hooks/useGeneration';
import { api3Call } from '../schema/api3/client';
import * as api3 from '../schema/api3/endpoints';
import { account } from '../schema/api3/endpoints';
import { accountBasicProfilesMock } from '../schema/api3/mocks/accountBasicProfilesMock';
import { accountMock } from '../schema/api3/mocks/accountMock';
import { api3PrivacyPreferencesMock } from '../schema/api3/mocks/privacyPreferencesMock';
import { PrivacyPreferenceData } from '../schema/api3/types/Api3PrivacyPreferences';
import { Api3Profile } from '../schema/api3/types/Api3Profile';
import { useApi3 } from '../schema/api3/useApi3';
import { useFetch } from '../services/NetworkService';
import { ChildrenOnly } from '../types/ChildrenOnly';
import { returnVoid } from '../util/returnVoid';
import { UserData, UserResult, IdentityProvider } from './UserData';
import { makeProfileObject } from './makeProfileObject';

export const UserContext = createContext<UserResult>({ loading: true, refetch: returnVoid });

export const useUserData = () => useContext(UserContext);

export const UserProvider = ({ children }: ChildrenOnly) => {
  const [generation, refetch] = useGeneration();
  const fetch = useFetch();
  const [profileUpdating, setProfileUpdating] = useState(false);
  const [profileUpdateError, setProfileUpdateError] = useState(false);

  const routeParams = useRouter()?.query;

  const bookingFormClient: BookingFormClient | undefined = routeParams?.bookingForm
    ? unsafeCoerce(String(routeParams?.bookingForm))
    : undefined;

  const displayAppointment = useDisplayAppointment(bookingFormClient);

  const accountData = useApi3(
    useMemo(() => {
      void generation;
      return api3.account.getAccount();
    }, [generation]),
  );

  const accountProfiles = useApi3(
    useMemo(() => {
      void generation;
      return api3.account.getBasicProfiles();
    }, [generation]),
  );

  const saveToAccount = useCallback(
    (updateableProfileData: Api3Profile) =>
      api3Call(fetch, api3.account.saveProfile(updateableProfileData)),
    [fetch],
  );

  const privacyPreferences = useApi3(
    useMemo(() => {
      void generation;
      return api3.account.getPrivacyPreferences();
    }, [generation]),
  );

  const updateProfile = useCallback<(profile: Api3Profile) => Promise<void>>(
    updateableProfileData => {
      setProfileUpdating(true);
      setProfileUpdateError(false);

      return saveToAccount(updateableProfileData)
        .then(() => {
          refetch?.();
        })
        .catch(() => setProfileUpdateError(true))
        .finally(() => setProfileUpdating(false));
    },
    [saveToAccount, refetch],
  );

  const updatePrivacyPreferences = useCallback<
    (privacyPreferences: PrivacyPreferenceData) => Promise<void>
  >(
    privacyPreferences => {
      setProfileUpdating(true);
      setProfileUpdateError(false);

      return api3Call(fetch, account.savePrivacyPreferences(privacyPreferences))
        .then(_ => {
          refetch?.();
        })
        .catch(() => setProfileUpdateError(true))
        .finally(() => setProfileUpdating(false));
    },
    [fetch, refetch],
  );

  const corporateGroupIdentityConfigResult = useCorporateGroupIdentityConfig();

  const corporateUserResult = useCorporateUserData();

  const heUserResult: UserResult = useMemo(() => {
    /** NB: If the user is logged out, we expect to see errors on the endpoints. */
    if (
      accountData.loading ||
      accountProfiles.loading ||
      privacyPreferences.loading ||
      profileUpdating
    )
      return { loading: true, refetch };

    if (accountData.error || privacyPreferences.error || accountProfiles.error)
      return { loading: false, refetch };

    if (profileUpdateError) return { loading: false, error: profileUpdateError, refetch };

    if (accountData.data && accountProfiles.data && privacyPreferences.data) {
      return {
        loading: false,
        data: makeUserObject(
          accountData.data,
          accountProfiles.data,
          privacyPreferences.data,
          updateProfile,
          updatePrivacyPreferences,
        ),
        refetch,
      };
    }

    return { loading: true, refetch };
  }, [
    accountData.loading,
    accountData.error,
    accountData.data,
    accountProfiles.loading,
    accountProfiles.error,
    accountProfiles.data,
    privacyPreferences.loading,
    privacyPreferences.error,
    privacyPreferences.data,
    profileUpdating,
    refetch,
    profileUpdateError,
    updateProfile,
    updatePrivacyPreferences,
  ]);

  const userState = useMemo(
    () =>
      mergeUserResult(
        corporateGroupIdentityConfigResult,
        heUserResult,
        corporateUserResult,
        displayAppointment,
      ),
    [corporateGroupIdentityConfigResult, heUserResult, corporateUserResult, displayAppointment],
  );

  return <UserContext.Provider value={{ ...userState, refetch }}>{children}</UserContext.Provider>;
};

export const mergeUserResult = (
  corporateGroupIdentityConfigResult: CorporateGroupIdentityConfigResult,
  userResult: UserResult,
  corporateUserResult: CorporateUserResult,
  displayAppointment: DisplayAppointment,
) => {
  // Return HE identity early if this is a non-platform booking
  if (DisplayAppointment.Platform !== displayAppointment) {
    return {
      identityProvider: IdentityProvider.HEALTHENGINE,
      loading: userResult.loading,
      data: userResult.data,
      error: userResult.error,
    };
  }

  let identityProvider;

  // Use corporate group identity if we have configuration loaded for it and this is not a network booking,
  // fall back to HE identity if not
  if (!corporateGroupIdentityConfigResult.loading) {
    identityProvider = corporateGroupIdentityConfigResult.data
      ? IdentityProvider.CORPORATE_GROUP
      : IdentityProvider.HEALTHENGINE;
  }

  if (IdentityProvider.CORPORATE_GROUP === identityProvider) {
    const corpData = corporateUserResult.data
      ? { ...corporateUserResult.data, profiles: [corporateUserResult.data] }
      : undefined;

    return {
      identityProvider,
      loading: corporateUserResult.loading,
      data: corpData,
    };
  }

  if (IdentityProvider.HEALTHENGINE === identityProvider) {
    return {
      identityProvider,
      loading: userResult.loading,
      data: userResult.data,
      error: userResult.error,
    };
  }

  return { identityProvider, loading: true, data: undefined };
};

export const makeUserObject = (
  accountData: typeof accountMock,
  accountProfiles: typeof accountBasicProfilesMock,
  privacyPreferences: typeof api3PrivacyPreferencesMock,
  updateProfile: (profile: Api3Profile) => Promise<void>,
  updatePrivacyPreferences: (privacyPreferences: PrivacyPreferenceData) => Promise<void>,
): UserData => {
  const profiles = accountProfiles.data.map(profile => makeProfileObject(profile));

  const { profileId, isPrimary: ignore, ...primaryProfile } =
    profiles.find(profile => profile.isPrimary) || {};

  // Profile sorting order copied from the old booking form
  const sortedProfiles = orderBy(
    profiles,
    ['isPrimary', 'firstName', 'lastName'],
    ['desc', 'asc', 'asc'],
  );

  return {
    userId: accountData.user_id ?? undefined,
    ...primaryProfile,
    primaryProfileId: profileId,
    privacyPreferences: privacyPreferences.data,
    profiles: sortedProfiles,
    updateProfile,
    updatePrivacyPreferences,
  };
};
